import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Building2,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  MapPin,
  Phone,
  PhoneCall,
  Plane,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { VietQRGenerator } from "@/components/VietQRGenerator";
import { QRPosterView } from "@/features/payments/qr-poster/QRPosterView";
import { logoForBrand } from "@/features/payments/qr-poster/brand";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { Badge } from "@/ui/badge";
import { Skeleton } from "@/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/select";
import { offpeakLabel } from "@/features/promos/offpeak";
import {
  checkPublicPromo,
  createPublicBooking,
  fetchPublicBookingStatus,
  fetchPublicAvailability,
  fetchPublicOffpeakNearby,
  fetchPublicOffpeakQuote,
  fetchPublicBookingBillingConfig,
  fetchPublicServices,
  PublicBookingError,
  type CustomerBookingInvoiceType,
  type PublicBookingCreateResponse,
  type PublicBookingPurpose,
  type PublicBookingService,
  type PublicBookingSlot,
  type PublicOffpeakNearbySuggestion,
  type PublicPromoCheck,
  type PublicUtm,
} from "./api";
import { publicBranchByCode, usePublicBranches, type BrandedBranch, type PublicBranch } from "./branches";
import { trackFunnel } from "./funnel";
import {
  bookingMoney,
  discountedTotal,
  addDiscounts,
  requiredAmount,
  selectedDeposit,
  selectedPrice,
} from "./pricing";
import { localPhoneFromNormalized } from "./search-params";
import {
  BookingInvoiceSection,
  invoiceTypeLabel,
  emptyInvoiceBuyer,
  invoiceBuyerIsComplete,
  type InvoiceBuyer,
} from "./BookingInvoiceSection";
import {
  emptyPersonBuyer,
  personBuyerIsComplete,
  personPayload,
  type PersonBuyer,
} from "./personalInvoiceBuyer";
import { nextConceptId } from "./serviceGroups";
import { ServicePicker } from "./ServicePicker";
import { PreShootNotesCard } from "./PreShootNotesCard";
import { purposeLogo } from "./purposeLogos";
import { TurnstileBox } from "./turnstile";
import { publicPageScrollClass as publicScrollClass } from "@/components/PublicPageScroll";

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";
const publicAction = import.meta.env.VITE_TURNSTILE_ACTION ?? "customer_booking";
const maxHorizonDays = 60;
const hasTurnstileConfig = siteKey.trim().length > 0;
// Mức giữ chỗ dự phòng khi máy chủ chưa trả cấu hình (cùng mặc định ở máy chủ).
const DEFAULT_PAYMENT_HOLD_MINUTES = 30;
// Sau khi mã hết hạn, trang còn hỏi trạng thái thưa trong vài giờ để bắt tiền
// chuyển khoản về trễ.
const LATE_PAYMENT_POLL_MS = 30_000;
const LATE_PAYMENT_WATCH_MS = 4 * 60 * 60 * 1000;
// Nền kem + monogram Gạo Nâu có voan mờ và vùng sáng/tối
// (class .booking-monogram-bg trong globals.css).
const publicPageScrollClass = `${publicScrollClass} booking-monogram-bg`;

type Props = {
  initialBranchCode?: string;
  /** Mã đi kèm link người giới thiệu (?ma=). Điền sẵn và tự kiểm hộ khách. */
  initialPromoCode?: string;
  /** Dấu máy affiliate đi kèm link từ AFF central. Ẩn khỏi UI, chỉ gửi kèm booking. */
  initialAffDauMay?: string;
  /** Tham số đo kênh từ link (utm_*) — gửi kèm payload, không hiện trên UI. */
  initialUtm?: PublicUtm;
  /**
   * Số điện thoại đi kèm link từ cổng khách (?sdt=). Chỉ ĐIỀN SẴN ô nhập —
   * không bỏ qua bước nào, khách vẫn sửa đè được (đặt hộ người khác).
   * Nhận cả dạng cổng khách "84…" lẫn dạng nội địa "0…".
   */
  initialPhone?: string;
};

export function PublicBookingPage({
  initialBranchCode,
  initialPromoCode,
  initialAffDauMay,
  initialUtm,
  initialPhone,
}: Props) {
  const { branches: publicBranches } = usePublicBranches();
  // Resolve against the live list so a deep link to a newly opened branch works.
  const initialBranch = initialBranchCode
    ? publicBranchByCode(initialBranchCode, publicBranches)
    : null;
  if (initialBranchCode && !initialBranch) return <UnknownBranch branchCode={initialBranchCode} />;
  return (
    <BookingFlow
      initialBranch={initialBranch}
      publicBranches={publicBranches}
      initialPromoCode={initialPromoCode ?? ""}
      initialAffDauMay={initialAffDauMay ?? ""}
      initialPhone={localPhoneFromNormalized(initialPhone ?? "")}
      {...(initialUtm ? { initialUtm } : {})}
    />
  );
}

function BookingFlow({
  initialBranch,
  publicBranches,
  initialPromoCode,
  initialAffDauMay,
  initialPhone,
  initialUtm,
}: {
  initialBranch: PublicBranch | null;
  publicBranches: readonly BrandedBranch[];
  initialPromoCode: string;
  initialAffDauMay: string;
  initialPhone: string;
  initialUtm?: PublicUtm;
}) {
  // Mốc "xem trang" của phễu đặt lịch. Bắn một lần cho mỗi phiên (trackFunnel
  // tự chặn trùng), không chờ kết quả — phép đo không được cản việc đặt lịch.
  useEffect(() => {
    trackFunnel("view", initialBranch?.code);
  }, [initialBranch?.code]);

  const today = useMemo(() => dateOnly(new Date()), []);
  const minDate = today;
  const maxDate = useMemo(() => addDays(today, maxHorizonDays), [today]);
  const [selectedBranch, setSelectedBranch] = useState<BrandedBranch | null>(initialBranch);
  const [selectedService, setSelectedService] = useState<PublicBookingService | null>(null);
  const [selectedPurposeId, setSelectedPurposeId] = useState<string>("");
  // Concept KHÔNG bắt buộc (CEO chốt 21/08/2026) → "" là trạng thái hợp lệ,
  // không phải "chưa điền xong". Không được đưa vào canContinueToTime.
  const [selectedConceptId, setSelectedConceptId] = useState<string>("");
  const [weekStart, setWeekStart] = useState(minDate);
  const weekEnd = useMemo(() => minDateOf(addDays(weekStart, 6), maxDate), [maxDate, weekStart]);
  const days = useMemo(() => daysBetween(weekStart, weekEnd), [weekEnd, weekStart]);
  const [selectedDate, setSelectedDate] = useState(formatDate(minDate));
  const [selectedSlot, setSelectedSlot] = useState<PublicBookingSlot | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState(initialPhone);
  const [countryCallingCode, setCountryCallingCode] = useState<"+84">("+84");
  const [email, setEmail] = useState("");
  const [paymentOption, setPaymentOption] = useState<"deposit" | "full">("deposit");
  // cá nhân là mặc định: phần lớn khách đi chụp cá nhân, hỏi mã số thuế trước là
  // dựng thêm một hàng rào cho số đông để phục vụ số ít.
  const [invoiceType, setInvoiceType] = useState<CustomerBookingInvoiceType>("personal");
  const [invoiceBuyer, setInvoiceBuyer] = useState<InvoiceBuyer>(emptyInvoiceBuyer);
  const [invoiceEshopCode, setInvoiceEshopCode] = useState("");
  // Người mua cá nhân (CEO 24/10/2026): mặc định không ghi tên; khách muốn
  // hoá đơn mang tên mình thì bỏ tích và gõ họ tên. Không hỏi CCCD ở đây.
  const [invoicePerson, setInvoicePerson] = useState<PersonBuyer>(emptyPersonBuyer);
  const [invoiceSubmitted, setInvoiceSubmitted] = useState(false);
  // Tách đôi có chủ đích: promoCode là chữ trong ô nhập, appliedPromoCode là mã
  // đang thực sự có hiệu lực. Gõ dở dang trong ô không được đổi số tiền.
  const [promoCode, setPromoCode] = useState(initialPromoCode);
  const [appliedPromoCode, setAppliedPromoCode] = useState(initialPromoCode.trim());
  const [promoResult, setPromoResult] = useState<PublicPromoCheck | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);
  // Nhớ đã hỏi máy chủ cho cặp (mã, giá gói) nào rồi, để hiệu ứng tự kiểm
  // không gọi lặp mỗi lần trang vẽ lại.
  const promoCheckedRef = useRef("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const requestRef = useRef({ payloadKey: "", clientRequestId: newClientRequestId() });
  // Kèm luôn clientRequestId đã gửi kèm đơn: màn thanh toán cần đúng mã của
  // lần tạo thành công, mà ref thì không được đọc trong lúc render.
  const [result, setResult] = useState<
    { booking: PublicBookingCreateResponse; clientRequestId: string } | null
  >(null);
  const [activeStep, setActiveStep] = useState<1 | 2 | 3 | 4>(1);

  const canPickSchedule = Boolean(selectedBranch);
  const selectedBranchCode = selectedBranch?.code ?? "";
  const servicesQ = useQuery({
    // Mã cơ sở nằm trong khoá cache vì nó lọc concept theo miền — đổi cơ sở
    // phải hỏi lại, nếu không khách ở Hà Nội vẫn thấy concept của miền Nam.
    queryKey: ["public-booking", "services", selectedBranchCode],
    queryFn: ({ signal }) => fetchPublicServices({ branchCode: selectedBranchCode, signal }),
    retry: 1,
  });

  const availabilityQ = useQuery({
    queryKey: ["public-booking", "availability", selectedBranchCode, formatDate(weekStart), formatDate(weekEnd)],
    enabled: canPickSchedule,
    queryFn: ({ signal }) =>
      fetchPublicAvailability({
        branchCode: selectedBranchCode,
        from: formatDate(weekStart),
        to: formatDate(weekEnd),
        signal,
      }),
    retry: 1,
  });

  const slotsForDate = useMemo(
    () => (availabilityQ.data ?? []).filter((slot) => slot.date === selectedDate),
    [availabilityQ.data, selectedDate],
  );
  const selectedSlotAvailable = useMemo(
    () =>
      selectedSlot &&
      selectedSlot.date === selectedDate &&
      slotsForDate.some((slot) => slot.startTime === selectedSlot.startTime && !slot.booked)
        ? selectedSlot
        : null,
    [selectedDate, selectedSlot, slotsForDate],
  );
  const hasOffpeakToday = slotsForDate.some((slot) => !slot.booked && slot.offpeakPct > 0);

  // Giờ Vàng: chỉ hỏi khi đã đủ cơ sở + gói + ô giờ còn trống. Hỏi khống vừa
  // chắc chắn bị máy chủ đá, vừa ghi thêm một dòng "lượt chào" vào nhật ký và
  // làm loãng tỉ lệ chào→chốt mà giám đốc dùng để chỉnh bậc thang.
  // Giờ Vàng nhường định mức cho mã, nên hỏi kèm phần của mã: đổi mã là hỏi lại.
  const promoDiscount = promoResult?.valid ? promoResult.discount : 0;
  const offpeakQuoteParams =
    selectedBranchCode && selectedSlotAvailable && selectedService
      ? {
          branchCode: selectedBranchCode,
          date: selectedSlotAvailable.date,
          startTime: selectedSlotAvailable.startTime,
          serviceCode: selectedService.code,
          ...(promoDiscount > 0 ? { otherDiscount: promoDiscount } : {}),
        }
      : null;
  const offpeakQuoteQ = useQuery({
    queryKey: ["public-booking", "offpeak-quote", offpeakQuoteParams],
    enabled: offpeakQuoteParams !== null,
    queryFn: ({ signal }) => {
      if (!offpeakQuoteParams) throw new Error("Thiếu tham số để hỏi ưu đãi Giờ Vàng");
      return fetchPublicOffpeakQuote({ ...offpeakQuoteParams, signal });
    },
    // Hỏi hụt KHÔNG được chặn khách đặt lịch: máy chủ vẫn là chốt cuối và vẫn
    // áp đúng mức giảm lúc tạo lịch hẹn, chỉ là trang không khoe trước.
    retry: false,
  });

  // Gợi ý chéo khu vực: cơ sở khác CÙNG MIỀN đang vắng hơn trong đúng ngày
  // khách đang xem. Chỉ gợi ý — không tự đổi cơ sở, không đổi mặc định.
  // Thuế suất do máy chủ chốt. Không có sẵn một giá trị dự phòng: đoán thuế
  // suất là hiện sai số tiền cho khách, nên hỏng cấu hình thì khóa nút đặt.
  const billingConfigQ = useQuery({
    queryKey: ["public-booking", "billing-config"],
    queryFn: ({ signal }) => fetchPublicBookingBillingConfig({ signal }),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const vatRateBps = billingConfigQ.data?.vatRateBps ?? 0;
  const billingReady = Boolean(billingConfigQ.data);
  // Thời gian giữ chỗ do máy chủ chốt; chưa có cấu hình thì dùng mức mặc định.
  const holdMinutes =
    billingConfigQ.data?.paymentHoldMinutes && billingConfigQ.data.paymentHoldMinutes > 0
      ? billingConfigQ.data.paymentHoldMinutes
      : DEFAULT_PAYMENT_HOLD_MINUTES;

  const offpeakNearbyQ = useQuery({
    queryKey: ["public-booking", "offpeak-nearby", selectedBranchCode, selectedDate],
    enabled: canPickSchedule,
    queryFn: ({ signal }) =>
      fetchPublicOffpeakNearby({ branchCode: selectedBranchCode, date: selectedDate, signal }),
    retry: false,
  });

  // Gói có mục đích chụp (hộ chiếu, visa, hãng bay…) thì giá đơn là giá của
  // mục đích khách chọn, không phải giá niêm yết của gói. Máy chủ chốt lại
  // đúng con số này khi tạo phiếu — chỗ này chỉ để khách thấy trước.
  const selectedPurpose =
    selectedService?.purposes?.find((p) => p.id === selectedPurposeId) ?? null;
  const needsPurpose = (selectedService?.purposes?.length ?? 0) > 0;
  const purposeReady = !needsPurpose || selectedPurpose !== null;
  // Gói có mục đích mà khách chưa chọn thì giá CHƯA có nghĩa — để 0 chứ không
  // mượn tạm giá niêm yết của gói. Mượn tạm là khách thấy 970.000 đ rồi tụt
  // xuống 900.000 đ khi bấm "Hộ chiếu", trông như giá ảo. Form đơn nội bộ cũng
  // đưa về 0 chờ chốt lại (features/schedule/components/detail/upsale.ts).
  const servicePrice = selectedPrice(selectedService, selectedPurpose) ?? 0;
  const serviceDeposit = selectedDeposit(selectedService, selectedPurpose) ?? 0;
  const selectedConcept =
    selectedService?.concepts?.find((concept) => concept.id === selectedConceptId) ?? null;
  // Kiểu ảnh đi cùng tên gói ở MỌI chỗ tóm tắt (thanh trên đầu, bước chọn giờ,
  // bước xác nhận) — khách thấy lại lựa chọn của mình mà không phải quay về
  // bước 2. Gói Profile không có mục đích nên hai nhãn không bao giờ chen nhau.
  const serviceLabel = selectedService
    ? [selectedService.name, selectedPurpose?.label, selectedConcept?.name].filter(Boolean).join(" · ")
    : "Chưa chọn";
  const runPromoCheck = useCallback(async (code: string, total: number, product: string) => {
    // Nhớ theo mã ĐÃ GỬI (không phải mã máy chủ trả về, vì máy chủ viết hoa
    // lại) — nếu nhớ theo mã trả về thì hiệu ứng dưới sẽ hỏi lại vô tận.
    promoCheckedRef.current = promoCheckKey(code, total, product);
    setPromoChecking(true);
    try {
      // `product` đi qua THAM SỐ chứ không đọc từ closure: hàm này có deps []
      // nên đọc selectedService bên trong là đóng băng gói của lần render đầu,
      // và rào phạm vi sẽ xét sai gói ngay sau khi khách đổi gói.
      setPromoResult(await checkPublicPromo({ code, total, ...(product ? { product } : {}) }));
    } catch {
      // Mã hỏng KHÔNG được chặn khách đặt lịch: coi như đặt không mã.
      setPromoResult({
        valid: false,
        code,
        label: "",
        discount: 0,
        message: "Chưa kiểm tra được mã lúc này",
      });
    } finally {
      setPromoChecking(false);
    }
  }, []);

  // Mã đi kèm link thì tự kiểm hộ khách. Mã đã áp cũng phải hỏi lại khi khách
  // đổi gói, vì phần giảm tính theo % giá gói — giữ số cũ là hiện sai tiền.
  useEffect(() => {
    if (!appliedPromoCode || servicePrice <= 0) return;
    if (
      promoCheckedRef.current ===
      promoCheckKey(appliedPromoCode, servicePrice, selectedService?.code ?? "")
    ) {
      return;
    }
    void runPromoCheck(appliedPromoCode, servicePrice, selectedService?.code ?? "");
  }, [appliedPromoCode, runPromoCheck, servicePrice, selectedService?.code]);

  // Máy chủ cộng mã với Giờ Vàng (QĐ 0511, CEO 1b). Trang mirror đúng luật đó để
  // không hiện một đằng thu một nẻo — nhưng con số cuối vẫn do máy chủ chốt lúc
  // tạo lịch hẹn.
  const offpeakQuote = offpeakQuoteQ.data?.applicable ? offpeakQuoteQ.data : null;
  const applied = addDiscounts(
    promoResult?.valid ? { code: promoResult.code, discount: promoDiscount } : null,
    offpeakQuote,
  );
  const totalAfterPromo = discountedTotal(servicePrice, applied.discount);
  const depositDue = selectedService
    ? requiredAmount(servicePrice, serviceDeposit, applied.discount, "deposit")
    : 0;
  // Toàn bộ số tiền gồm VAT, ở một chỗ. Cọc KHÔNG cộng VAT — thuế rơi vào
  // phần trả nốt tại studio.
  const money = bookingMoney({
    price: servicePrice,
    depositAmount: serviceDeposit,
    discount: applied.discount,
    vatRateBps,
    paymentOption,
  });
  // Cơ sở hàng xóm đáng mời nhất: bỏ chính cơ sở đang xem, bỏ cơ sở trang
  // không tra ra tên (gợi ý một cái tên khách không nhận ra thì thà đừng gợi),
  // còn lại lấy mức giảm sâu nhất — banner là một lời mời, không phải một bảng.
  const nearbyPick = useMemo(() => {
    let best: { suggestion: PublicOffpeakNearbySuggestion; branch: PublicBranch } | null = null;
    for (const suggestion of offpeakNearbyQ.data ?? []) {
      if (suggestion.branchCode === selectedBranchCode) continue;
      const branch = publicBranchByCode(suggestion.branchCode, publicBranches);
      if (!branch) continue;
      if (!best || suggestion.pct > best.suggestion.pct) best = { suggestion, branch };
    }
    return best;
  }, [offpeakNearbyQ.data, publicBranches, selectedBranchCode]);

  const resetSchedule = () => {
    setSelectedSlot(null);
    requestRef.current = { payloadKey: "", clientRequestId: newClientRequestId() };
  };
  const handleTurnstileExpired = useCallback(() => setTurnstileToken(""), []);
  const handleTurnstileError = useCallback(() => {
    setTurnstileToken("");
    setTurnstileAttempt((attempt) => attempt + 1);
    toast.error("Không tải được xác minh bảo mật");
  }, []);

  const createM = useMutation({
    mutationFn: () => {
      if (!selectedBranch) throw new Error("Vui lòng chọn cơ sở");
      if (!selectedService) throw new Error("Vui lòng chọn dịch vụ");
      if (!selectedSlotAvailable) throw new Error("Vui lòng chọn giờ chụp");
      if (hasTurnstileConfig && !turnstileToken) throw new Error("Vui lòng hoàn tất xác minh bảo mật");
      // Gửi lên ĐÚNG mã khách gõ, KHÔNG gửi mã Giờ Vàng: máy chủ tự hỏi Giờ
      // Vàng rồi cộng vào, còn ô promoCode nó đem đi tra bảng mã tay — nhét
      // GIOVANG- vào đó là bị từ chối cả lượt đặt.
      // Đọc thẳng promoResult (không qua addDiscounts): mã giới thiệu giảm đúng
      // 0đ vẫn phải đi kèm đơn, nếu không người giới thiệu mất hoa hồng — mà hàm
      // cộng ưu đãi thì coi 0đ là "không có gì".
      const appliedPromo = promoResult?.valid ? promoResult.code : "";
      const payloadKey = bookingPayloadKey(selectedBranch.code, selectedService.code, selectedPurposeId, selectedConceptId, name, phone, email, paymentOption, selectedSlotAvailable, appliedPromo, invoicePayloadKey(invoiceType, invoiceBuyer, invoiceEshopCode, invoicePerson));
      if (requestRef.current.payloadKey !== payloadKey) {
        requestRef.current = { payloadKey, clientRequestId: newClientRequestId() };
      }
      const request = {
        name,
        phone,
        countryCallingCode,
        branchCode: selectedBranch.code,
        serviceCode: selectedService.code,
        paymentOption,
        date: selectedSlotAvailable.date,
        startTime: selectedSlotAvailable.startTime,
        clientRequestId: requestRef.current.clientRequestId,
        ...(selectedPurposeId ? { purposeId: selectedPurposeId } : {}),
        ...(selectedConceptId ? { conceptId: selectedConceptId } : {}),
        ...(appliedPromo ? { promoCode: appliedPromo } : {}),
        ...(initialAffDauMay.trim() ? { affDauMay: initialAffDauMay.trim() } : {}),
        invoiceType,
        // Chỉ gửi thông tin doanh nghiệp khi khách chọn hóa đơn VAT. Máy chủ
        // bỏ qua và lưu NULL với cá nhân, nên gửi kèm chỉ là đẩy dữ liệu cá nhân
        // đi xa hơn mức cần.
        ...(invoiceType === "business"
          ? {
              invoiceCompanyName: invoiceBuyer.companyName.trim(),
              invoiceTaxCode: invoiceBuyer.taxCode.trim(),
              invoiceCompanyAddress: invoiceBuyer.companyAddress.trim(),
              invoiceEmail: invoiceBuyer.email.trim(),
            }
          : {
              ...(invoiceEshopCode.trim() ? { invoiceEshopCode: invoiceEshopCode.trim() } : {}),
              ...personPayload(invoiceType, invoicePerson, false),
            }),
        ...(initialUtm ?? {}),
        ...(hasTurnstileConfig ? { turnstileToken } : {}),
      };
      return createPublicBooking(email.trim() ? { ...request, email: email.trim() } : request);
    },
    onSuccess: (data) => {
      trackFunnel("submitted", selectedBranch?.code);
      setResult({ booking: data, clientRequestId: requestRef.current.clientRequestId });
      toast.success("Đã tạo mã thanh toán giữ lịch");
      void availabilityQ.refetch();
    },
    onError: (error) => {
      const message = publicBookingMessage(error);
      toast.error(message);
      setTurnstileToken("");
      setTurnstileAttempt((attempt) => attempt + 1);
      if (error instanceof PublicBookingError && error.code === "slot_unavailable") {
        void availabilityQ.refetch();
      }
      if (error instanceof PublicBookingError && error.code === "version_conflict") {
        requestRef.current = { payloadKey: "", clientRequestId: newClientRequestId() };
      }
    },
  });

  if (result && selectedBranch) {
    return (
      <PaymentState
        branch={selectedBranch}
        service={selectedService}
        result={result.booking}
        clientRequestId={result.clientRequestId}
        holdMinutes={holdMinutes}
        onNew={() => window.location.reload()}
      />
    );
  }

  const canSubmit = Boolean(
    selectedBranch &&
      selectedService &&
      name.trim() &&
      phone.trim() &&
      selectedSlotAvailable &&
      billingReady &&
      invoiceBuyerIsComplete(invoiceType, invoiceBuyer) &&
      personBuyerIsComplete(invoiceType, invoicePerson) &&
      (!hasTurnstileConfig || turnstileToken) &&
      !createM.isPending,
  );
  const canContinueToService = Boolean(selectedBranch);
  const canContinueToTime = Boolean(selectedBranch && selectedService && purposeReady);
  const canContinueToConfirm = Boolean(selectedSlotAvailable);
  const canPrevWeek = weekStart > minDate;
  const canNextWeek = weekEnd < maxDate;
  // Đổi gói — hoặc bỏ gói khi khách chuyển sang nhóm sản phẩm khác. Giờ đã
  // chọn phải bỏ theo vì mỗi gói một thời lượng, và phiên tạo đơn đánh số lại
  // để lần bấm trước không bị coi là gửi trùng.
  const applyService = (service: PublicBookingService | null, purposeId: string) => {
    setSelectedService(service);
    setSelectedPurposeId(purposeId);
    // Concept thuộc về GÓI: giữ lại khi đổi gói là đơn mang kiểu ảnh của gói khác.
    setSelectedConceptId((current) => nextConceptId(service, current));
    setSelectedSlot(null);
    requestRef.current = { payloadKey: "", clientRequestId: newClientRequestId() };
  };
  const moveWeek = (nextWeekStart: Date) => {
    setWeekStart(nextWeekStart);
    setSelectedDate(formatDate(nextWeekStart));
    setSelectedSlot(null);
  };

  return (
    <main className={publicPageScrollClass}>
      <section className="mx-auto flex min-h-dvh w-full max-w-5xl flex-col px-3 py-4 sm:px-6 sm:py-5 lg:px-8">
        <header className="rounded-3xl bg-[var(--color-brand)] p-5 text-white shadow-brand sm:p-7">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <img src="/favicon.jpg" alt="Gạo Nâu" className="h-11 w-11 rounded-xl object-cover" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-white/65">Gạo Nâu Profile Studio</div>
                  <h1 className="mt-1 text-2xl font-semibold sm:text-3xl">Đặt lịch chụp</h1>
                </div>
              </div>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-white/95">
                Đi từng bước thật gọn: chọn nơi chụp, chọn giờ, rồi thanh toán giữ lịch trong {holdMinutes} phút.
              </p>
            </div>
            <div className="grid gap-2 rounded-2xl bg-white/10 p-4 text-sm sm:min-w-72">
              <SummaryLine icon={<MapPin className="h-4 w-4" />} label="Cơ sở" value={selectedBranch?.shortLabel ?? "Chưa chọn"} />
              <SummaryLine icon={<Sparkles className="h-4 w-4" />} label="Dịch vụ" value={serviceLabel} />
              <a href="/booking/me" className="mt-1 rounded-xl border border-white/20 px-3 py-2 text-center text-xs font-semibold text-white hover:bg-white/10">
                Xem booking của tôi
              </a>
            </div>
          </div>
        </header>

        {promoResult && (
          <div
            data-testid="promo-ribbon"
            className={[
              "mt-4 rounded-2xl border p-4 text-sm",
              promoResult.valid
                ? "border-success/30 bg-success/10 text-success-text"
                : "border-border bg-surface-2 text-muted-foreground",
            ].join(" ")}
          >
            {promoResult.valid ? (
              <>
                🎁 Bạn được giảm <strong>{promoResult.label}</strong> với mã{" "}
                <strong>{promoResult.code}</strong>
                {promoDiscount > 0 && selectedService
                  ? ` — tiết kiệm ${formatVND(promoDiscount)}`
                  : ""}
              </>
            ) : (
              <>
                Mã <strong>{promoResult.code}</strong> chưa dùng được
                {promoResult.message ? `: ${promoResult.message}` : ""}. Bạn vẫn đặt lịch bình thường
                nhé.
              </>
            )}
          </div>
        )}

        <section className="mt-5 min-w-0 rounded-3xl border bg-surface p-3 shadow-sm sm:p-5 lg:p-6">
          <StepProgress
            activeStep={activeStep}
            onStep={(step) => {
              if (step === 1) setActiveStep(1);
              if (step === 2 && canContinueToService) setActiveStep(2);
              if (step === 3 && canContinueToTime) setActiveStep(3);
              if (step === 4 && canContinueToTime && canContinueToConfirm) setActiveStep(4);
            }}
            canOpenService={canContinueToService}
            canOpenTime={canContinueToTime}
            canOpenConfirm={canContinueToTime && canContinueToConfirm}
          />

          <div className="mt-6 min-h-[520px] min-w-0">
            {activeStep === 1 && (
              <div className="space-y-6">
                <StepTitle index={1} title="Chọn ngày" description="Chọn cơ sở và ngày bạn muốn chụp. Giờ trống sẽ hiện ở bước sau." />

                <div>
                  <div className="mb-3 text-sm font-semibold">Cơ sở</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {publicBranches.map((branch) => {
                      const selected = selectedBranch?.code === branch.code;
                      return (
                        <button
                          key={branch.code}
                          type="button"
                          onClick={() => {
                            setSelectedBranch(branch);
                            resetSchedule();
                          }}
                          className={[
                            "min-h-24 rounded-2xl border p-4 text-left transition",
                            selected
                              ? "border-primary bg-brand/10 text-primary shadow-sm"
                              : "border-border bg-surface hover:bg-surface-2",
                          ].join(" ")}
                        >
                          <div className="flex items-start gap-3">
                            {branch.photo ? (
                              <img
                                src={branch.photo}
                                alt={branch.shortLabel}
                                loading="lazy"
                                className="h-12 w-12 shrink-0 rounded-lg border border-border/60 object-cover"
                              />
                            ) : (
                              <span className="mt-0.5 rounded-xl bg-surface-2 p-2 text-primary">
                                <MapPin className="h-4 w-4" />
                              </span>
                            )}
                            <span>
                              <span className="block font-semibold">{branch.shortLabel}</span>
                              <span className="mt-1 block text-sm text-muted-foreground">{branch.address}</span>
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">Ngày chụp</div>
                      <div className="text-xs text-muted-foreground">Có thể đặt từ hôm nay đến 60 ngày tới.</div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="icon"
                        variant="outline"
                        disabled={!canPrevWeek}
                        onClick={() => moveWeek(maxDateOf(addDays(weekStart, -7), minDate))}
                        aria-label="Tuần trước"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        disabled={!canNextWeek}
                        onClick={() => moveWeek(minDateOf(addDays(weekStart, 7), maxDate))}
                        aria-label="Tuần sau"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                    {days.map((day) => {
                      const value = formatDate(day);
                      const selected = selectedDate === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            setSelectedDate(value);
                            setSelectedSlot(null);
                          }}
                          className={[
                            "min-h-16 rounded-2xl border px-3 py-2 text-left transition",
                            selected ? "border-primary bg-brand/10 text-primary shadow-sm" : "border-border bg-surface hover:bg-surface-2",
                          ].join(" ")}
                        >
                          <span className="block text-xs font-medium text-muted-foreground">{weekdayLabel(day)}</span>
                          <span className="mt-1 block font-semibold">
                            {day.getDate()}/{day.getMonth() + 1}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeStep === 2 && (
              <div className="space-y-5">
                <StepTitle
                  index={2}
                  title="Chọn dịch vụ"
                  description="Chọn lần lượt: nhóm sản phẩm → gói → mục đích chụp. Giá và tiền cọc được lấy từ bảng sản phẩm trong hệ thống."
                />
                <div className="rounded-2xl bg-surface-2 p-4 text-sm">
                  <InfoRow label="Cơ sở" value={selectedBranch?.shortLabel ?? "Chưa chọn"} />
                  <InfoRow label="Ngày" value={selectedDate} />
                </div>

                {servicesQ.isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-12 rounded-2xl" />
                    <Skeleton className="h-12 rounded-2xl" />
                    <Skeleton className="h-32 rounded-2xl" />
                  </div>
                ) : servicesQ.isError ? (
                  <div className="rounded-2xl border border-danger/30 bg-danger/10 p-4 text-sm text-danger-text">
                    {publicBookingMessage(servicesQ.error)}
                    <Button className="mt-3" variant="outline" onClick={() => void servicesQ.refetch()}>
                      Tải lại bảng giá
                    </Button>
                  </div>
                ) : (
                  <ServicePicker
                    services={servicesQ.data ?? []}
                    selected={selectedService}
                    selectedPurpose={selectedPurpose}
                    onSelect={applyService}
                    onClear={() => applyService(null, "")}
                    purposeSection={
                      selectedService ? (
                        <PurposePicker
                          service={selectedService}
                          selectedId={selectedPurposeId}
                          onSelect={(purpose) => setSelectedPurposeId(purpose.id)}
                        />
                      ) : null
                    }
                    selectedConceptId={selectedConceptId}
                    onConceptSelect={setSelectedConceptId}
                    corporateSection={<CorporateContact />}
                  />
                )}
              </div>
            )}

            {activeStep === 3 && (
              <div className="space-y-5">
                <StepTitle index={3} title="Chọn giờ" description="Slot “Đã kín” không thể chọn." />
                <div className="rounded-2xl bg-surface-2 p-4 text-sm">
                  <InfoRow label="Cơ sở" value={selectedBranch?.shortLabel ?? "Chưa chọn"} />
                  <InfoRow label="Dịch vụ" value={serviceLabel} />
                  <InfoRow label="Ngày" value={selectedDate} />
                </div>

                {hasOffpeakToday && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-2xl border p-3 [background:var(--gv-bg)] [border-color:var(--gv-border)]">
                    <span className="rounded-full border px-2.5 py-0.5 text-xs font-bold [border-color:var(--gv-border)] [color:var(--gv-dark)]">
                      ⚡ Có Giờ Vàng hôm nay
                    </span>
                    <span className="min-w-0 flex-1 text-xs leading-5 [color:var(--gv-dark)]">
                      Ô nền vàng đang giảm giá, khung càng vắng giảm càng sâu. Bạn không phải gõ mã — chọn giờ là tự có.
                    </span>
                  </div>
                )}

                <div>
                  {availabilityQ.isLoading ? (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <Skeleton key={i} className="h-12 rounded-xl" />
                      ))}
                    </div>
                  ) : availabilityQ.isError ? (
                    <div className="rounded-2xl border border-danger/30 bg-danger/10 p-4 text-sm text-danger-text">
                      {publicBookingMessage(availabilityQ.error)}
                      <Button className="mt-3" variant="outline" onClick={() => void availabilityQ.refetch()}>
                        Thử tải lại
                      </Button>
                    </div>
                  ) : slotsForDate.length === 0 ? (
                    <div className="rounded-2xl border bg-surface-2 p-4 text-sm text-muted-foreground">
                      Chưa có slot mở cho ngày này. Bạn thử chọn ngày khác nhé.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                      {slotsForDate.map((slot) => {
                        const selected = !slot.booked && selectedSlot?.date === slot.date && selectedSlot.startTime === slot.startTime;
                        // Ô đã kín thì không dán badge dù máy chủ có trả %:
                        // mời chào một ô bấm không được chỉ làm khách bực.
                        const offpeakPct = slot.booked ? 0 : slot.offpeakPct;
                        return (
                          <button
                            key={`${slot.date}-${slot.startTime}`}
                            type="button"
                            disabled={slot.booked}
                            onClick={() => {
                              setSelectedSlot(slot);
                              trackFunnel("slot_selected", selectedBranch?.code);
                            }}
                            className={[
                              "flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl border px-2 py-2 text-sm font-semibold transition",
                              selected ? "border-primary bg-primary text-primary-foreground shadow-sm" : "border-border bg-surface hover:bg-surface-2",
                              !selected && offpeakPct > 0 ? "[background:var(--gv-bg)] [border-color:var(--gv-border)]" : "",
                              slot.booked ? "cursor-not-allowed border-border bg-muted/30 text-muted-foreground line-through opacity-65" : "",
                            ].join(" ")}
                          >
                            <span>{slot.booked ? "Đã kín" : slot.startTime}</span>
                            {offpeakPct > 0 && (
                              <span className="rounded-full px-1.5 py-0.5 text-[11px] font-bold leading-none [background:var(--gv)] [color:var(--gv-ink)]">
                                ⚡ −{offpeakPct}%
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Tông xanh "thông tin" theo bản vẽ CEO duyệt, cố ý KHÔNG dùng
                    tông hổ phách: đây là lời mách nước sang cơ sở khác, không
                    phải ưu đãi đang áp cho ô giờ khách vừa chọn. */}
                {nearbyPick && (
                  <div
                    data-testid="offpeak-nearby"
                    className="flex flex-wrap items-center gap-3 rounded-2xl border border-info/30 bg-info/10 p-3 text-sm"
                  >
                    <span aria-hidden="true" className="text-lg">
                      💡
                    </span>
                    <p className="min-w-0 flex-1 leading-6">
                      Cơ sở <strong>{nearbyPick.branch.shortLabel}</strong> (cùng {nearbyPick.branch.regionLabel}) đang
                      vắng hơn — khung{" "}
                      <strong>
                        {nearbyPick.suggestion.startTime}–{nearbyPick.suggestion.endTime}
                      </strong>{" "}
                      có <strong>⚡ −{nearbyPick.suggestion.pct}%</strong>.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="min-h-11 shrink-0"
                      onClick={() => {
                        // CHỈ gợi ý: cơ sở chỉ đổi khi khách tự bấm.
                        setSelectedBranch(nearbyPick.branch);
                        resetSchedule();
                      }}
                    >
                      Xem lịch bên đó
                    </Button>
                  </div>
                )}
              </div>
            )}

            {activeStep === 4 && (
              <div className="grid min-w-0 gap-5 lg:grid-cols-[0.9fr_1.1fr]">
                <div>
                  <StepTitle index={4} title="Xác nhận thông tin" description="Kiểm tra thông tin, chọn hình thức thanh toán rồi tạo mã giữ lịch." />
                  <div className="mt-4 rounded-2xl bg-surface-2 p-4 text-sm">
                    <InfoRow label="Cơ sở" value={selectedBranch?.shortLabel ?? "Chưa chọn"} />
                    <InfoRow label="Dịch vụ" value={serviceLabel} />
                    <InfoRow
                      label="Giá dịch vụ"
                      value={selectedService && purposeReady ? formatVND(servicePrice) : "Chưa chọn"}
                    />
                    {selectedService && applied.discount > 0 && (
                      <>
                        {applied.codeDiscount > 0 && (
                          <InfoRow
                            testId="summary-discount-row"
                            label={`Giảm giá (${applied.code})`}
                            value={`− ${formatVND(applied.codeDiscount)}`}
                          />
                        )}
                        {applied.offpeakDiscount > 0 && (
                          <InfoRow
                            testId="summary-offpeak-row"
                            label={offpeakLabel(offpeakQuote?.pct ?? 0)}
                            value={`− ${formatVND(applied.offpeakDiscount)}`}
                          />
                        )}
                        <InfoRow label="Còn lại" value={formatVND(totalAfterPromo)} />
                      </>
                    )}
                    <InfoRow label="Ngày" value={selectedSlotAvailable ? selectedSlotAvailable.date : "Chưa chọn"} />
                    <InfoRow
                      label="Giờ"
                      value={
                        selectedSlotAvailable
                          ? `${selectedSlotAvailable.startTime}–${selectedSlotAvailable.endTime}`
                          : "Chưa chọn"
                      }
                    />
                    {applied.offpeakDiscount > 0 && selectedSlotAvailable && (
                      <p
                        data-testid="offpeak-note"
                        className="mt-3 border-t border-dashed pt-3 text-xs font-semibold leading-5 [border-color:var(--gv-border)] [color:var(--gv-dark)]"
                      >
                        ⚡ Đang áp {offpeakLabel(offpeakQuote?.pct ?? 0)} cho khung{" "}
                        {selectedSlotAvailable.startTime} ({formatVND(applied.offpeakDiscount)}) ·
                        giữ giá tới khi bạn xác nhận đặt lịch
                        {promoDiscount > 0 ? " · cộng thêm với mã bạn nhập" : ""}
                      </p>
                    )}
                  </div>
                </div>

                <form
                  className="space-y-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setInvoiceSubmitted(true);
                    createM.mutate();
                  }}
                >
                  <div className="space-y-1.5">
                    <Label htmlFor="public-booking-name">Họ tên</Label>
                    <Input
                      id="public-booking-name"
                      className="min-h-11"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="Tên của bạn"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="public-booking-phone">Số điện thoại</Label>
                    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2">
                      <Select value={countryCallingCode} onValueChange={(value) => setCountryCallingCode(value as "+84")}>
                        <SelectTrigger className="min-h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="+84">+84 VN</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        id="public-booking-phone"
                        className="min-h-11"
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        placeholder="090..."
                        inputMode="tel"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="public-booking-email">
                      Email{" "}
                      <span className="text-muted-foreground">
                        để nhận hướng dẫn trước buổi chụp (không bắt buộc)
                      </span>
                    </Label>
                    <Input
                      id="public-booking-email"
                      className="min-h-11"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@example.com"
                      inputMode="email"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="public-booking-promo">
                      Mã ưu đãi hoặc mã giới thiệu{" "}
                      <span className="text-muted-foreground">(nếu có)</span>
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="public-booking-promo"
                        className="min-h-11"
                        value={promoCode}
                        onChange={(event) => {
                          setPromoCode(event.target.value);
                          // Sửa ô nhập là bỏ mã đang áp: nếu không, khách xoá mã
                          // đi mà tiền vẫn giảm, rồi gửi đơn kèm mã đã xoá.
                          if (event.target.value.trim().toUpperCase() !== appliedPromoCode.toUpperCase()) {
                            setAppliedPromoCode("");
                            setPromoResult(null);
                          }
                        }}
                        placeholder="Nhập mã của bạn"
                        autoCapitalize="characters"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="min-h-11 shrink-0"
                        disabled={!promoCode.trim() || !selectedService || promoChecking}
                        onClick={() => {
                          // Xoá dấu "đã hỏi rồi": khách bấm Áp dụng là muốn hỏi
                          // lại, kể cả với đúng mã vừa hỏi hụt lúc nãy.
                          promoCheckedRef.current = "";
                          setAppliedPromoCode(promoCode.trim());
                          void runPromoCheck(
                            promoCode.trim(),
                            servicePrice,
                            selectedService?.code ?? "",
                          );
                        }}
                      >
                        {promoChecking ? "Đang kiểm…" : "Áp dụng"}
                      </Button>
                    </div>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      Mỗi lần đặt dùng được một mã, gõ loại nào cũng vào ô này:
                      <br />
                      <strong className="font-medium text-foreground">Mã ưu đãi</strong> — chương
                      trình khuyến mãi chung của Gạo Nâu, ai cũng dùng được.
                      <br />
                      <strong className="font-medium text-foreground">Mã giới thiệu</strong> — mã
                      riêng của một bạn cộng tác viên hoặc nhân viên. Bạn vẫn được giảm như thường,
                      và người giới thiệu bạn được ghi công.
                    </p>
                  </div>

                  <BookingInvoiceSection
                    invoiceType={invoiceType}
                    onInvoiceTypeChange={(value) => {
                      setInvoiceType(value);
                      // Quay về cá nhân là xoá sạch thông tin doanh nghiệp: giữ
                      // lại thì lần gửi sau vẫn mang dữ liệu khách tưởng đã bỏ.
                      if (value !== "business") {
                        setInvoiceBuyer(emptyInvoiceBuyer);
                        setInvoiceSubmitted(false);
                      } else {
                        // Đối xứng: hóa đơn VAT không mang mã cá nhân, giữ lại
                        // thì lần gửi sau vẫn đẩy đi thứ khách tưởng đã bỏ.
                        setInvoiceEshopCode("");
                        setInvoicePerson(emptyPersonBuyer);
                      }
                    }}
                    buyer={invoiceBuyer}
                    onBuyerChange={setInvoiceBuyer}
                    eshopCode={invoiceEshopCode}
                    onEshopCodeChange={setInvoiceEshopCode}
                    person={invoicePerson}
                    onPersonChange={setInvoicePerson}
                    forCustomer
                    showErrors={invoiceSubmitted}
                  />

                  <div className="space-y-2">
                    <Label>Thanh toán giữ lịch</Label>
                    <div className="grid gap-2">
                      <PaymentOptionButton
                        selected={paymentOption === "deposit"}
                        title="Thanh toán tiền cọc"
                        description={selectedService ? formatVND(depositDue) : "Chọn dịch vụ để xem số tiền cọc"}
                        onClick={() => setPaymentOption("deposit")}
                      />
                      <PaymentOptionButton
                        selected={paymentOption === "full"}
                        title="Thanh toán đủ"
                        description={
                          selectedService
                            ? `${formatVND(money.payableTotal)} (đã gồm VAT)`
                            : "Chọn dịch vụ để xem giá"
                        }
                        onClick={() => setPaymentOption("full")}
                      />
                    </div>
                  </div>

                  {selectedService && billingReady && (
                    <dl className="space-y-1.5 rounded-2xl border bg-muted/30 p-3 text-sm">
                      <MoneyRow label="Giá dịch vụ" value={formatVND(servicePrice)} />
                      {applied.discount > 0 && (
                        <MoneyRow label="Ưu đãi" value={`- ${formatVND(applied.discount)}`} />
                      )}
                      <MoneyRow
                        label={`VAT ${(vatRateBps / 100).toLocaleString("vi-VN")}%`}
                        value={formatVND(money.vatAmount)}
                      />
                      <MoneyRow label="Tổng cộng" value={formatVND(money.payableTotal)} strong />
                      <MoneyRow label="Chuyển ngay giữ lịch" value={formatVND(money.paymentRequired)} />
                      <MoneyRow
                        label="Còn lại trả tại studio"
                        value={formatVND(money.remaining)}
                      />
                    </dl>
                  )}

                  {billingConfigQ.isError && (
                    <div className="rounded-2xl border border-danger/30 bg-danger/10 p-3 text-sm text-danger-text">
                      Chưa tải được thuế suất VAT nên tạm thời không nhận đặt lịch. Bạn vui lòng thử
                      lại sau ít phút hoặc liên hệ CSKH.
                    </div>
                  )}

                  {hasTurnstileConfig && (
                    <TurnstileBox
                      key={turnstileAttempt}
                      siteKey={siteKey}
                      action={publicAction}
                      onToken={setTurnstileToken}
                      onExpire={handleTurnstileExpired}
                      onError={handleTurnstileError}
                    />
                  )}

                  {createM.isError && (
                    <div className="rounded-2xl border border-danger/30 bg-danger/10 p-3 text-sm text-danger-text">
                      {publicBookingMessage(createM.error)}
                    </div>
                  )}

                  {/* Lý do phải cọc, đặt sát nút bấm — đây là chỗ khách hay
                      chần chừ nhất (CEO chốt 27/08/2026). */}
                  <p className="text-xs text-muted-foreground">
                    Cọc để giữ ekip riêng cho bạn đúng khung giờ.
                  </p>

                  <Button type="submit" className="min-h-12 w-full text-base" disabled={!canSubmit}>
                    {createM.isPending ? "Đang tạo mã…" : "Tạo mã thanh toán giữ lịch"}
                  </Button>

                  <p className="text-xs leading-5 text-muted-foreground">
                    Slot được giữ {holdMinutes} phút để bạn thanh toán. Sau khi tiền về, hệ thống tự chốt lịch chụp và CSKH sẽ liên hệ hỗ trợ tiếp.
                  </p>
                </form>
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={activeStep === 1 || createM.isPending}
              onClick={() => setActiveStep((step) => (step > 1 ? ((step - 1) as 1 | 2 | 3 | 4) : 1))}
            >
              Quay lại
            </Button>
            {activeStep === 1 && (
              <Button type="button" className="min-h-11" disabled={!canContinueToService} onClick={() => setActiveStep(2)}>
                Tiếp tục chọn dịch vụ
              </Button>
            )}
            {activeStep === 2 && (
              <Button type="button" className="min-h-11" disabled={!canContinueToTime} onClick={() => setActiveStep(3)}>
                Tiếp tục chọn giờ
              </Button>
            )}
            {activeStep === 3 && (
              <Button type="button" className="min-h-11" disabled={!canContinueToConfirm} onClick={() => setActiveStep(4)}>
                Tiếp tục xác nhận
              </Button>
            )}
          </div>
        </section>
      </section>
    </main>
  );
}

function StepTitle({ index, title, description }: { index: number; title: string; description: string }) {
  return (
    <div>
      <h2 className="flex items-center gap-2 text-lg font-semibold">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-sm text-primary-foreground">
          {index}
        </span>
        {title}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function StepProgress({
  activeStep,
  onStep,
  canOpenService,
  canOpenTime,
  canOpenConfirm,
}: {
  activeStep: 1 | 2 | 3 | 4;
  onStep: (step: 1 | 2 | 3 | 4) => void;
  canOpenService: boolean;
  canOpenTime: boolean;
  canOpenConfirm: boolean;
}) {
  const steps: Array<{ step: 1 | 2 | 3 | 4; label: string; unlocked: boolean }> = [
    { step: 1, label: "Ngày", unlocked: true },
    { step: 2, label: "Dịch vụ", unlocked: canOpenService },
    { step: 3, label: "Giờ", unlocked: canOpenTime },
    { step: 4, label: "Xác nhận", unlocked: canOpenConfirm },
  ];
  return (
    <nav aria-label="Tiến trình đặt lịch" className="grid gap-2 sm:grid-cols-4">
      {steps.map((item) => {
        const active = activeStep === item.step;
        const done = activeStep > item.step;
        return (
          <button
            key={item.step}
            type="button"
            disabled={!item.unlocked}
            onClick={() => onStep(item.step)}
            className={[
              "flex min-h-12 items-center gap-3 rounded-2xl border px-3 py-2 text-left transition",
              active
                ? "border-primary bg-brand/10 text-primary shadow-sm"
                : done
                  ? "border-success/30 bg-success/10 text-success-text"
                  : "border-border bg-surface-2 text-muted-foreground",
              !item.unlocked ? "cursor-not-allowed opacity-50" : "hover:bg-surface-2",
            ].join(" ")}
            aria-current={active ? "step" : undefined}
          >
            <span
              className={[
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
                active
                  ? "bg-primary text-primary-foreground"
                  : done
                    ? "bg-success text-success-foreground"
                    : "bg-muted text-muted-foreground",
              ].join(" ")}
            >
              {done ? <CheckCircle2 className="h-4 w-4" /> : item.step}
            </span>
            <span>
              <span className="block text-xs font-medium">Bước {item.step}</span>
              <span className="block font-semibold">{item.label}</span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

function SummaryLine({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-start gap-2">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="text-xs text-white/65">{label}</div>
        <div className="break-words font-semibold text-white">{value}</div>
      </div>
    </div>
  );
}

function PaymentState({
  branch,
  service,
  result,
  clientRequestId,
  holdMinutes,
  onNew,
}: {
  branch: PublicBranch;
  service: PublicBookingService | null;
  result: PublicBookingCreateResponse;
  clientRequestId: string;
  holdMinutes: number;
  onNew: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  const [mountedAt] = useState(() => Date.now());
  const payment = result.payment;
  const watchFrom = (() => {
    const expiry = payment?.expiresAt ?? result.paymentExpiresAt;
    const ms = expiry ? new Date(expiry).getTime() : Number.NaN;
    return Number.isFinite(ms) ? ms : mountedAt;
  })();
  const statusQ = useQuery({
    queryKey: ["public-booking", "status", result.id, clientRequestId],
    queryFn: ({ signal }) => fetchPublicBookingStatus({ id: result.id, clientRequestId, signal }),
    enabled: Boolean(result.id && clientRequestId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === "converted" || data?.paymentStatus === "paid") return false;
      // Tiền có thể về SAU khi mã hết hạn (khách chuyển khoản trễ): máy chủ vẫn
      // tự lên đơn. Trang tiếp tục hỏi thưa một lúc để khách thấy lịch đã xác
      // nhận, thay vì dừng hẳn và để họ đặt lại lịch trùng.
      if (data?.status === "payment_expired") {
        return Date.now() - watchFrom < LATE_PAYMENT_WATCH_MS ? LATE_PAYMENT_POLL_MS : false;
      }
      return 5_000;
    },
  });
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  const currentStatus = statusQ.data?.status ?? result.status;
  const currentPaymentStatus = statusQ.data?.paymentStatus ?? payment?.status ?? "pending";
  const isPaid = currentStatus === "converted" || currentPaymentStatus === "paid";
  const isExpired = currentStatus === "payment_expired" || currentPaymentStatus === "expired" || currentPaymentStatus === "underpaid";
  const expiresAt = payment?.expiresAt ?? result.paymentExpiresAt;
  const secondsLeft = expiresAt ? Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000)) : 0;

  return (
    <main className={`${publicPageScrollClass} px-3 py-4 sm:px-4 sm:py-8`}>
      <section className="mx-auto flex min-h-[calc(100dvh-2rem)] w-full max-w-3xl items-start sm:min-h-[calc(100dvh-4rem)] sm:items-center">
        <div className="min-w-0 w-full rounded-3xl border bg-surface p-4 shadow-sm sm:p-8">
          <div className="text-center">
            <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${isPaid ? "bg-success/10 text-success-text" : isExpired ? "bg-danger/10 text-danger-text" : "bg-brand/10 text-primary"}`}>
              {isPaid ? <CheckCircle2 className="h-9 w-9" /> : <CreditCard className="h-9 w-9" />}
            </div>
            <h1 className="mt-5 text-2xl font-semibold">
              {isPaid ? "Đã thanh toán và giữ lịch thành công" : isExpired ? "Mã thanh toán đã hết hạn" : "Thanh toán để giữ lịch"}
            </h1>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              {isPaid
                ? "Lịch chụp của bạn đã được chốt. CSKH Gạo Nâu sẽ liên hệ bạn để hoàn tất các bước chuẩn bị."
                : isExpired
                  ? currentPaymentStatus === "underpaid"
                    ? "Số tiền chuyển khoản chưa đủ. Booking vẫn được lưu, bạn đừng đặt lại lịch — hãy chờ CSKH liên hệ hỗ trợ."
                    : "Booking vẫn được lưu để CSKH hỗ trợ. Nếu bạn đã chuyển khoản, lịch sẽ tự xác nhận — bạn đừng đặt lại lịch. Cần hỗ trợ thì chờ CSKH liên hệ."
                  : `Vui lòng chuyển khoản đúng nội dung bên dưới trong ${holdMinutes} phút. Khi Sepay báo tiền về, lịch sẽ tự xác nhận.`}
            </p>
          </div>

          <div className="mt-6 grid min-w-0 gap-5 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)]">
            {payment && !isPaid && !isExpired ? (
              <div className="min-w-0 rounded-3xl border bg-surface-2 p-3 text-center sm:p-4">
                {/* Một mẫu duy nhất — khách tự quét, không cần chọn mẫu để gửi
                    ai. Dựng ảnh hỏng thì rơi về mã QR trơn bên dưới. */}
                <QRPosterView
                  bank={payment.bank}
                  amount={payment.amount}
                  memo={payment.memo}
                  logo={logoForBrand(service?.brandCode)}
                  fallback={
                    <VietQRGenerator
                      bankBin={payment.bank.bankBin}
                      accountNumber={payment.bank.accountNumber}
                      accountHolder={payment.bank.accountHolder}
                      amount={payment.amount}
                      memo={payment.memo}
                      size={240}
                    />
                  }
                />
                <div className="mt-3 text-sm font-semibold text-primary">Còn {formatDuration(secondsLeft)}</div>
              </div>
            ) : (
              <div className="rounded-3xl border bg-surface-2 p-6 text-center text-sm text-muted-foreground">
                {isPaid ? "Thanh toán đã được xác nhận." : "Không còn mã QR đang hoạt động."}
              </div>
            )}

            <div className="min-w-0 space-y-4">
              <div className="rounded-2xl bg-surface-2 p-4 text-sm">
                <InfoRow label="Mã giữ chỗ" value={result.publicReference} />
                <InfoRow label="Cơ sở" value={branch.shortLabel} />
                <InfoRow
                  label="Dịch vụ"
                  value={[result.serviceName ?? service?.name ?? "—", result.purposeLabel]
                    .filter(Boolean)
                    .join(" · ")}
                />
                <InfoRow label="Ngày giờ" value={`${result.date} · ${result.startTime}-${result.endTime}`} />
                <InfoRow label="Giá dịch vụ" value={formatVND(result.productPrice)} />
                {result.promoDiscount - (result.offpeakDiscount ?? 0) > 0 && (
                  <InfoRow
                    label="Ưu đãi"
                    value={`- ${formatVND(result.promoDiscount - (result.offpeakDiscount ?? 0))}`}
                  />
                )}
                {(result.offpeakDiscount ?? 0) > 0 && (
                  <InfoRow label="⚡ Giờ Vàng" value={`- ${formatVND(result.offpeakDiscount ?? 0)}`} />
                )}
                {/* Số của MÁY CHỦ, không phải bản xem trước: đây là con số
                    khách sẽ bị trừ, nên phải đọc từ đơn đã tạo. */}
                <InfoRow
                  label={`VAT ${(result.vatRateBps / 100).toLocaleString("vi-VN")}%`}
                  value={formatVND(result.vatAmount)}
                />
                <InfoRow label="Tổng cộng" value={formatVND(result.payableTotal)} />
                <InfoRow
                  label="Hóa đơn"
                  value={invoiceTypeLabel(result.invoiceType) ?? "—"}
                />
                {result.invoiceEshopCode && (
                  <InfoRow label="Mã hóa đơn cá nhân" value={result.invoiceEshopCode} />
                )}
                <InfoRow label="Cần thanh toán" value={formatVND(payment?.amount ?? result.paymentRequiredAmount)} />
                {result.remainingAmount > 0 && (
                  <InfoRow label="Còn lại trả tại studio" value={formatVND(result.remainingAmount)} />
                )}
                <InfoRow label="Trạng thái" value={statusLabel(currentStatus, currentPaymentStatus)} />
              </div>

              {payment && !isPaid && !isExpired && (
                <div className="rounded-2xl border bg-surface p-4 text-sm">
                  <div className="flex items-center gap-2 font-semibold">
                    <Phone className="h-4 w-4 text-primary" />
                    Thông tin chuyển khoản
                  </div>
                  <div className="mt-3 space-y-2">
                    <CopyLine label="Ngân hàng/BIN" value={payment.bank.bankBin} />
                    <CopyLine label="Số tài khoản" value={payment.bank.accountNumber} />
                    <CopyLine label="Chủ tài khoản" value={payment.bank.accountHolder} />
                    <CopyLine label="Số tiền" value={String(payment.amount)} displayValue={formatVND(payment.amount)} />
                    <CopyLine label="Nội dung" value={payment.memo} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Dặn dò CHỈ hiện sau khi tiền đã về. Lúc còn đang quét mã QR mà
              nhét thêm chữ vào là làm nhiễu đúng bước quan trọng nhất
              (CEO chốt 27/08/2026). */}
          {isPaid && <PreShootNotesCard notes={result.preShootNotes} />}

          <Button className="mt-6 min-h-11 w-full" onClick={onNew}>
            Đặt lịch khác
          </Button>
        </div>
      </section>
    </main>
  );
}

/** Một dòng trong bảng tiền: nhãn trái, số phải, không xuống dòng ở 375px. */
function MoneyRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={strong ? "font-medium text-foreground" : "text-muted-foreground"}>{label}</dt>
      <dd className={strong ? "font-semibold tabular-nums" : "tabular-nums"}>{value}</dd>
    </div>
  );
}

function PaymentOptionButton({
  selected,
  title,
  description,
  onClick,
}: {
  selected: boolean;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-2xl border p-3 text-left transition",
        selected ? "border-primary bg-brand/10 text-primary" : "border-border bg-surface hover:bg-surface-2",
      ].join(" ")}
    >
      <span className="block font-semibold">{title}</span>
      <span className="mt-1 block text-sm text-muted-foreground">{description}</span>
    </button>
  );
}

function CopyLine({ label, value, displayValue }: { label: string; value: string; displayValue?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-xl bg-surface-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <button
        type="button"
        className="min-h-8 min-w-0 break-all text-left font-semibold text-primary sm:text-right"
        onClick={() => {
          void navigator.clipboard?.writeText(value);
          toast.success("Đã copy");
        }}
      >
        {displayValue ?? value}
      </button>
    </div>
  );
}

function UnknownBranch({ branchCode }: { branchCode: string }) {
  // Offer the branches that do take bookings, so a stale link is a dead end
  // with a way forward rather than a dead end.
  const { branches: publicBranches } = usePublicBranches();
  return (
    <main className={`${publicPageScrollClass} px-4 py-8`}>
      <section className="mx-auto max-w-xl rounded-3xl border bg-surface p-6 shadow-sm">
        <Badge variant="yellow">Booking</Badge>
        <h1 className="mt-4 text-2xl font-semibold">Chi nhánh chưa được mở booking</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Link `/booking/{branchCode}` hiện không hợp lệ hoặc chi nhánh chưa được mở cho khách tự đặt lịch.
        </p>
        <div className="mt-5 grid gap-2">
          <a className="rounded-xl border px-4 py-3 text-sm font-semibold hover:bg-surface-2" href="/booking">
            Xem tất cả cơ sở
          </a>
          {publicBranches.map((branch) => (
            <a key={branch.code} className="rounded-xl border px-4 py-3 text-sm font-medium hover:bg-surface-2" href={`/booking/${branch.code}`}>
              {branch.shortLabel}
            </a>
          ))}
        </div>
      </section>
    </main>
  );
}

/** Khối chọn mục đích chụp. Gói ảnh thẻ tiếp viên hàng không thì mỗi mục là
 *  một hãng bay có giá riêng, nên đổi tiêu đề và hiện logo hãng cho dễ nhận. */
function PurposePicker({
  service,
  selectedId,
  onSelect,
}: {
  service: PublicBookingService;
  selectedId: string;
  onSelect: (purpose: PublicBookingPurpose) => void;
}) {
  const purposes = service.purposes ?? [];
  if (purposes.length === 0) return null;
  const airline = service.code.startsWith("cv_tvhk");
  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-semibold">{airline ? "3. Chọn hãng bay" : "3. Mục đích chụp"}</h3>
        <p className="text-sm text-muted-foreground">
          {airline
            ? "Mỗi hãng có tiêu chuẩn ảnh riêng nên giá khác nhau. Bạn chọn hãng đang ứng tuyển nhé."
            : "Mỗi mục đích có tiêu chuẩn ảnh riêng nên giá khác nhau."}
        </p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {purposes.map((purpose) => {
          const selected = purpose.id === selectedId;
          const logo = purposeLogo(purpose.id);
          return (
            <button
              key={purpose.id}
              type="button"
              onClick={() => onSelect(purpose)}
              className={[
                // min-w-0: ô trong lưới mặc định không co nhỏ hơn nội dung, nên
                // nhãn dài ("Emirates / Cathay / EVA / Qatar") đẩy thẻ lòi khỏi
                // khung 30px trên điện thoại.
                "flex min-h-14 min-w-0 items-center gap-3 rounded-2xl border px-3 py-2 text-left transition",
                selected
                  ? "border-primary bg-brand/10 text-primary shadow-sm"
                  : "border-border bg-surface hover:bg-surface-2",
              ].join(" ")}
            >
              {logo ? (
                <img
                  src={logo}
                  alt=""
                  aria-hidden
                  className="h-8 w-16 shrink-0 object-contain"
                  loading="lazy"
                />
              ) : (
                <span className="flex h-8 w-16 shrink-0 items-center justify-center text-muted-foreground">
                  <Plane className="h-4 w-4" />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{purpose.label}</span>
                <span className="block text-xs text-muted-foreground">
                  {formatVND(purpose.price)} · cọc {formatVND(purpose.depositAmount)}
                </span>
              </span>
              {selected && <Check className="h-4 w-4 shrink-0" />}
            </button>
          );
        })}
      </div>

      {/* Điều DUY NHẤT được phép chặn khách trước lúc trả tiền (CEO chốt
          27/08/2026): khách tưởng một buổi chụp dùng được cho cả hộ chiếu lẫn
          visa, tới lúc trả ảnh mới vỡ ra. Mọi dặn dò khác đợi sau khi cọc. */}
      {(selectedId === "ho_chieu" || selectedId === "visa") && (
        <p className="rounded-2xl border border-warning/40 bg-warning/10 p-3 text-xs leading-5">
          Hộ chiếu và visa không dùng chung một ảnh. Cần cả hai thì bạn đặt hai lượt chụp nhé.
        </p>
      )}
    </div>
  );
}

/** Gói doanh nghiệp không niêm yết giá trên trang khách — mỗi hợp đồng một
 *  báo giá riêng, nên chỉ mời khách liên hệ. */
function CorporateContact() {
  return (
    <div className="rounded-2xl border border-border bg-surface-2 p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 rounded-xl bg-surface p-2 text-primary">
          <Building2 className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold">Chụp cho doanh nghiệp</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Ảnh nhân sự, ảnh tập thể, ảnh sự kiện, quay dựng — báo giá riêng theo số lượng người và
            địa điểm. Bạn gọi cho Gạo Nâu để được tư vấn nhé.
          </p>
          <a
            href="tel:+84775243530"
            className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"
          >
            <PhoneCall className="h-4 w-4" />
            Liên hệ báo giá · (+84) 775 243 530
          </a>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div
      data-testid={testId}
      className="flex min-w-0 justify-between gap-3 border-b border-border/70 py-2 last:border-0"
    >
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-right font-medium">{value}</span>
    </div>
  );
}

function publicBookingMessage(error: unknown): string {
  if (error instanceof PublicBookingError) {
    switch (error.code) {
      case "slot_unavailable":
        return "Slot này vừa kín. Mình đã tải lại lịch, bạn chọn giờ khác nhé.";
      case "active_booking_exists":
        return "Số điện thoại này đang có lịch chờ/xác nhận. CSKH sẽ hỗ trợ bạn xử lý.";
      case "version_conflict":
        return "Yêu cầu gửi lại không khớp thông tin cũ. Bạn thử chọn lại lịch và gửi mới nhé.";
      case "rate_limited":
        return error.retryAfterSeconds
          ? `Bạn gửi hơi nhanh. Vui lòng thử lại sau khoảng ${error.retryAfterSeconds} giây.`
          : "Bạn gửi hơi nhanh. Vui lòng thử lại sau.";
      case "service_unavailable":
        return "Hệ thống booking đang tạm đóng hoặc chưa sẵn sàng. Bạn vui lòng thử lại sau.";
      case "csrf_origin_missing":
      case "csrf_origin_disallowed":
        return "Trang đặt lịch này chưa được mở để nhận lịch. Bạn vui lòng báo CSKH nhé.";
      default:
        return error.message || "Không xử lý được yêu cầu booking.";
    }
  }
  if (error instanceof Error) return error.message;
  return "Không xử lý được yêu cầu booking.";
}

function bookingPayloadKey(
  branchCode: string,
  serviceCode: string,
  // Đổi mục đích (hãng bay) là đổi số tiền — cùng lý do với mã ưu đãi bên dưới.
  purposeId: string,
  // Concept không đổi số tiền nhưng ĐỔI NỘI DUNG ĐƠN: thiếu nó thì khách quay
  // lại chọn kiểu ảnh khác rồi gửi lại sẽ bị coi là gửi trùng, và đơn giữ
  // nguyên concept cũ.
  conceptId: string,
  name: string,
  phone: string,
  email: string,
  paymentOption: string,
  slot: PublicBookingSlot,
  // Có mã ưu đãi cũng là một đơn khác (số tiền khác) — thiếu nó thì khách nhập
  // mã rồi gửi lại sẽ bị coi là gửi trùng và giữ nguyên số tiền cũ.
  promoCode: string,
  // Đổi cách xuất hóa đơn hoặc sửa thông tin bên mua là một đơn KHÁC: máy chủ
  // so đúng các trường này khi quyết định gửi trùng, nên thiếu chúng ở đây thì
  // khách sửa mã số thuế rồi gửi lại sẽ bị từ chối vì lệch dữ liệu cũ.
  invoice: string,
) {
  return [branchCode, serviceCode, purposeId, conceptId, name.trim(), phone.trim(), email.trim(), paymentOption, slot.date, slot.startTime, promoCode, invoice].join("|");
}

/** Chuỗi hóa lựa chọn hóa đơn theo ĐÚNG cách máy chủ chuẩn hóa: cá nhân bỏ hết
 *  thông tin doanh nghiệp, VAT thì cắt khoảng trắng và hạ chữ thường email. */
function invoicePayloadKey(
  invoiceType: CustomerBookingInvoiceType,
  buyer: InvoiceBuyer,
  eshopCode: string,
  person: PersonBuyer,
) {
  // Mã cá nhân nằm trong khóa: thiếu nó thì khách sửa mã rồi gửi lại sẽ trúng
  // nhánh phát lại của máy chủ và mã mới bị bỏ đi trong im lặng. Người mua cá
  // nhân cũng vậy: máy chủ coi gửi lặp đổi tên là xung đột.
  if (invoiceType !== "business") {
    const p = personPayload(invoiceType, person, false);
    return ["personal", eshopCode.trim(), p.invoiceBuyerKind, p.invoicePersonName, p.invoicePersonAddress].join("~");
  }
  return [
    "business",
    buyer.companyName.trim(),
    buyer.taxCode.trim(),
    buyer.companyAddress.trim(),
    buyer.email.trim().toLowerCase(),
  ].join("~");
}

// Khoá "đã kiểm mã này chưa". Gồm CẢ MÃ GÓI, không chỉ mã + giá: rào phạm vi
// (promo migration 0256) xét theo gói, nên đổi sang một gói CÙNG GIÁ mà khác
// thương hiệu vẫn phải hỏi lại. Thiếu gói trong khoá thì khách giữ nguyên số
// giảm của gói cũ trên một gói mã không được phép áp — và chỉ tới nhịp tạo đơn
// mới vỡ, lúc tiền đã chuyển.
export function promoCheckKey(code: string, total: number, product: string) {
  return `${code}|${total}|${product}`;
}

function newClientRequestId() {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function dateOnly(value: Date) {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function addDays(value: Date, days: number) {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

function minDateOf(a: Date, b: Date) {
  return a <= b ? a : b;
}

function maxDateOf(a: Date, b: Date) {
  return a >= b ? a : b;
}

function daysBetween(from: Date, to: Date) {
  const out: Date[] = [];
  for (let day = from; day <= to; day = addDays(day, 1)) out.push(day);
  return out;
}

function formatDate(value: Date) {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function weekdayLabel(value: Date) {
  return new Intl.DateTimeFormat("vi-VN", { weekday: "short" }).format(value);
}

function formatVND(value: number | undefined) {
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND", maximumFractionDigits: 0 }).format(value ?? 0);
}

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function statusLabel(status: string | undefined, paymentStatus: string | undefined) {
  if (status === "converted" || paymentStatus === "paid") return "Đã thanh toán · đã chốt lịch";
  if (paymentStatus === "underpaid") return "Thanh toán thiếu · CSKH sẽ xử lý";
  if (status === "payment_expired" || paymentStatus === "expired") return "Hết hạn thanh toán";
  if (status === "pending_payment") return "Đang chờ thanh toán";
  return status ?? "Đang xử lý";
}
